// Internal Amadeus request builders. Translates the canonical
// CheckoutRequest (providers/types.js) into the wire shape Amadeus's
// flight-order BE proxy expects.
//
// This module is internal to the amadeus/ folder — nothing outside it
// should know about flightCreateOrderBody, addresseeName, etc. UI code
// hands a CheckoutRequest to dispatcher.book() and stays provider-agnostic.

import { deep_clone } from '@/utilities/';
import { is_child } from '@liveflight/utilities/travelers';

// Shared Amadeus contact tail — applied identically to the order-level
// contact and every traveler's per-pax contact.
export const STANDARD_CONTACT_DEFAULTS = {
  purpose: 'STANDARD',
  language: 'IT',
};

export const build_contact = (contact_details) => ({
  addresseeName: {
    firstName: contact_details.firstname,
    middleName: contact_details.middlename,
    lastName: contact_details.lastname,
    secondLastName: contact_details.second_lastname,
  },
  ...STANDARD_CONTACT_DEFAULTS,
  phones: [
    {
      deviceType: 'MOBILE',
      countryCallingCode: contact_details.country_calling_code,
      number: contact_details.phone,
    },
  ],
  emailAddress: contact_details.email,
  address: {
    lines: [contact_details.address],
    postalCode: contact_details.zipcode,
    cityName: contact_details.city,
    countryCode: contact_details.country_code,
  },
});

export const build_traveler = (traveler, contact_details) => ({
  id: traveler.id,
  dateOfBirth: traveler.birth,
  name: {
    firstName: traveler.firstname,
    middleName: traveler.middlename,
    lastName: traveler.lastname,
    secondLastName: traveler.second_lastname,
  },
  gender: traveler.gender,
  contact: {
    ...STANDARD_CONTACT_DEFAULTS,
    emailAddress: traveler.email,
    phones: [
      {
        deviceType: 'MOBILE',
        countryCallingCode: contact_details.country_calling_code,
        number: contact_details.phone,
      },
    ],
  },
});

const DEFAULT_CHILD_LIMIT = 11;
/**
 * Translate the generic UI payload to the
 * Amadeus getFlightOffersBody body.
 * @param {object} payload
 * @returns {object}
 */
export const build_search_request = (payload) => {
  const request = deep_clone(payload);
  request.travelers = payload.travelers.map((traveler) => {
    if (!is_child(traveler)) return traveler;
    // Missing age → can't prove under the limit, treat as ADULT.
    const age_n = Number(traveler.age);
    const over_child_limit =
      !Number.isFinite(age_n) || age_n > DEFAULT_CHILD_LIMIT;
    if (!over_child_limit) return traveler;
    const parsed = { ...traveler, travelerType: 'ADULT' };
    delete parsed.age;
    return parsed;
  });
  return { getFlightOffersBody: request };
};

/**
 * Amadeus's flight-order envelope. `flightOffers` is always a one-element
 * array (the selected offer), with `_raw` unwrapped so Amadeus's BE
 * receives its native shape.
 *
 * @param {Object} flight_offer
 * @param {Object} request canonical CheckoutRequest
 * @returns {Object}
 */
export const build_flight_order_body = (flight_offer, request) => {
  const { contact: contact_details = {}, travelers = [] } = request || {};
  const { _raw: raw_offer } = flight_offer || {};
  if (!raw_offer) {
    throw new Error(
      '[AmadeusProvider] build_flight_order_body: flight_offer._raw missing — Amadeus /orders rejects the canonical shape'
    );
  }
  return {
    data: {
      type: 'flight-order',
      flightOffers: [raw_offer],
      travelers: travelers.map((t) => build_traveler(t, contact_details)),
      contacts: [build_contact(contact_details)],
    },
  };
};

/**
 * Full wire body for `service_handler('book', body)`:
 *   - bookingData.{live_price, flightCreateOrderBody, payments}
 *   - guest envelope spread flat at the top level (Salabam BE convention,
 *     shared with alpitour / liveeps / other sites).
 *
 * @param {Object} flight_offer
 * @param {Object} request canonical CheckoutRequest
 * @returns {Object}
 */
export const build_book_body = (flight_offer, request) => {
  const {
    guest = {},
    livePrice = '',
    payments = {},
    automatedCheckInRequested = false,
  } = request || {};
  const body = {
    bookingData: {
      live_price: livePrice,
      flightCreateOrderBody: build_flight_order_body(flight_offer, request),
      payments,
    },
  };
  if (automatedCheckInRequested) {
    body.bookingData.autocheckinRequested = true;
  }
  Object.keys(guest).forEach((key) => {
    body[key] = guest[key];
  });
  return body;
};
